import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Settings2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HISTORICAL_LEVELS = [
  { code: "diploma",  name: "Diploma",  numeric_rank: 4 },
  { code: "bachelor", name: "Bachelor", numeric_rank: 6 },
  { code: "master",   name: "Master",   numeric_rank: 7 },
  { code: "doctoral", name: "Doctoral", numeric_rank: 8 },
];

/**
 * Lightweight admin-only editor that lets us retro-tag legacy candidates
 * (or correct a mistake) by setting their Framework + Qualification Level
 * after the fact. Triggers `/candidates/{id}/framework-level` which keeps
 * `final_level` in sync so existing reports/badges keep working.
 */
export default function CandidateFrameworkLevelEditor({ candidate, onSaved }) {
  const [open, setOpen] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [framework, setFramework] = useState(candidate?.framework_id || "");
  const [level, setLevel] = useState(
    candidate?.qualification_level_code || candidate?.final_level || ""
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/frameworks`, { withCredentials: true });
        setFrameworks(r.data || []);
      } catch (_) { /* ignore */ }
    })();
  }, [open]);

  const chosen = frameworks.find((f) => f.framework_id === framework);
  const levels = chosen?.levels?.length ? chosen.levels : HISTORICAL_LEVELS;

  const save = async () => {
    if (!framework || !level) {
      toast.error("Please choose both a framework and a level.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/candidates/${candidate.candidate_id}/framework-level`,
        { framework_id: framework, qualification_level_code: level },
        { withCredentials: true }
      );
      toast.success("Framework & level updated.");
      setOpen(false);
      if (onSaved) onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="edit-framework-level-btn"
        >
          <Settings2 className="w-4 h-4 mr-2" />
          Edit Framework &amp; Level
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Framework &amp; Qualification Level</DialogTitle>
          <DialogDescription>
            Set or correct this candidate's competency framework and qualification level.
            This drives badge generation and the wording used in the published reports.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="fl-framework">Framework</Label>
            <Select
              value={framework || ""}
              onValueChange={(v) => { setFramework(v); setLevel(""); }}
            >
              <SelectTrigger id="fl-framework" data-testid="edit-fl-framework" className="mt-1.5">
                <SelectValue placeholder="Select framework" />
              </SelectTrigger>
              <SelectContent>
                {frameworks.map((f) => (
                  <SelectItem key={f.framework_id} value={f.framework_id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="fl-level">Qualification Level</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger id="fl-level" data-testid="edit-fl-level" className="mt-1.5">
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                {levels.map((lvl) => (
                  <SelectItem key={lvl.code} value={lvl.code}>{lvl.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!chosen?.levels?.length && (
              <p className="text-xs text-slate-500 mt-1.5">
                This framework hasn't been configured with custom levels — showing the 4 default levels.
                You can edit per-framework levels on the Frameworks page.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || !framework || !level}
            data-testid="edit-fl-save-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
